import { useState, useEffect, useCallback } from 'react';
import { DataTable, Column } from '../components/DataTable';
import { CompanyForm } from '../components/CompanyForm';
import { LogoUpload } from '../components/LogoUpload';
import { companyService, Company, CompanyCreatePayload, CompanyUpdatePayload } from '../services/companies';
import { ApiError } from '../services/api';

type ViewMode = 'list' | 'create' | 'edit';

export function CompaniesPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await companyService.list();
      setCompanies(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  function showSuccess(message: string) {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  function getErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        const details = err.details as Record<string, string> | undefined;
        if (details?.field === 'client_code') {
          return 'Client code is already in use. Please choose a different code.';
        }
        if (details?.message === 'has dependent records') {
          return 'Cannot delete company: it has associated users or files. Remove dependent records first.';
        }
        return err.message;
      }
      if (err.status === 422) {
        const details = err.details as { fields?: Record<string, string> } | undefined;
        if (details?.fields) {
          return Object.values(details.fields).join('. ');
        }
        return err.message;
      }
      return err.message;
    }
    return err instanceof Error ? err.message : 'An unexpected error occurred';
  }

  async function handleCreate(data: CompanyCreatePayload | CompanyUpdatePayload) {
    setError(null);
    setIsSubmitting(true);
    try {
      await companyService.create(data as CompanyCreatePayload);
      setViewMode('list');
      showSuccess('Company created successfully');
      await fetchCompanies();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUpdate(data: CompanyCreatePayload | CompanyUpdatePayload) {
    if (!selectedCompany) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await companyService.update(selectedCompany.id, data as CompanyUpdatePayload);
      setViewMode('list');
      setSelectedCompany(null);
      showSuccess('Company updated successfully');
      await fetchCompanies();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(company: Company) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${company.usual_name}"? This action cannot be undone.`,
    );
    if (!confirmed) return;

    setError(null);
    try {
      await companyService.delete(company.id);
      showSuccess('Company deleted successfully');
      await fetchCompanies();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }

  async function handleLogoUpload(file: File) {
    if (!selectedCompany) return;
    setIsUploading(true);
    setError(null);
    try {
      const updated = await companyService.uploadLogo(selectedCompany.id, file);
      setSelectedCompany(updated);
      showSuccess('Logo uploaded successfully');
      await fetchCompanies();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  }

  function handleEdit(company: Company) {
    setSelectedCompany(company);
    setViewMode('edit');
    setError(null);
  }

  function handleCancel() {
    setViewMode('list');
    setSelectedCompany(null);
    setError(null);
  }

  const columns: Column<Company>[] = [
    { key: 'usual_name', header: 'Name' },
    { key: 'client_code', header: 'Client Code', render: (c) => <code>{c.client_code}</code> },
    { key: 'domain', header: 'Domain' },
    {
      key: 'logo_s3_path',
      header: 'Logo',
      render: (c) => (c.logo_s3_path ? '✓' : '—'),
      width: '60px',
    },
  ];

  if (viewMode === 'create') {
    return (
      <div>
        <div className="page-header">
          <h1>Companies</h1>
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <CompanyForm
          onSubmit={handleCreate}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  if (viewMode === 'edit' && selectedCompany) {
    return (
      <div>
        <div className="page-header">
          <h1>Companies</h1>
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        {successMessage && <div className="alert alert-success" role="status">{successMessage}</div>}
        <CompanyForm
          company={selectedCompany}
          onSubmit={handleUpdate}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
        <div className="form__separator" />
        <LogoUpload
          onUpload={handleLogoUpload}
          currentLogoPath={selectedCompany.logo_s3_path}
          isUploading={isUploading}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header__row">
          <div>
            <h1>Companies</h1>
            <p>Manage client companies and their portal areas.</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => { setViewMode('create'); setError(null); }}
          >
            Add Company
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {successMessage && <div className="alert alert-success" role="status">{successMessage}</div>}

      <DataTable<Company>
        columns={columns}
        data={companies}
        keyField="id"
        loading={loading}
        emptyMessage="No companies registered yet."
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
}
